import { memo } from "react";
import { MiButton, MiStackedTypography } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  PERMISSIONS,
} from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { mdiThumbUpOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { ORDERS_DEFAULT_FIELD_WIDTH } from "../../ordersConstants";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import { useScheduledOrderActionsViewModel } from "./useScheduledOrderActionsViewModel";

/**
 * `ScheduledOrderActions` displays and manages actions related to a scheduled order,
 * including confirming the order and viewing additional options.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.id - Unique identifier for the scheduled order
 * @param {string|Date} props.approvedStartDate - The approved start date of the order
 * @param {string|Date} props.modifiedOn - The last modification date of the order
 * @param {Function} props.onRefresh - Callback to refresh data after actions are performed
 *
 * @returns {JSX.Element} A component with date display, confirm button, and action menu
 */
export const ScheduledOrderActions = memo((props) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const canConfirmOrder = hasPermission(PERMISSIONS.ORDER.CONFIRM);

  const { id, approvedStartDate, modifiedOn, onRefresh } = props;
  const { isLoading, confirmOrder } = useScheduledOrderActionsViewModel({
    id,
    modifiedOn,
    onRefresh,
  });

  return (
    <Box display="flex" flexWrap="wrap" gap={3} alignItems="center">
      <MiStackedTypography
        title="Approved Date"
        titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
        description={
          approvedStartDate
            ? formatDate(approvedStartDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)
            : "--"
        }
        descriptionProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
      />
      {canConfirmOrder && (
        <MiButton
          title="Confirm"
          variant={BUTTON_VARIANTS.OUTLINED}
          disabled={isLoading}
          startIcon={{
            path: mdiThumbUpOutline,
            iconSize: 0.8,
            iconColor: theme.palette.primary.main,
          }}
          onClick={confirmOrder}
        />
      )}
      <OrdersListActionMenu
        menuViewConfig={{
          Reschedule: true,
          Hold: true,
          Cancel: true,
          OrderActivityDetails: true,
          ForceComplete: true,
          Push24h: false,
          UndoApprove: true,
        }}
        {...props}
      />
    </Box>
  );
});
